import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  LuChevronDown,
  LuMail,
  LuMessageCircle,
  LuLayers,
  LuMic,
  LuShieldCheck,
} from 'react-icons/lu';
import XiPageShell from '../components/xi/XiPageShell';

const FAQS = [
  {
    q: 'How does emotion prediction work?',
    a: 'Tap Predict Emotion, allow microphone access, and speak for eight seconds. Xi analyses the recording on the server and returns the detected emotional state. If the recording is silent or unclear, you will be asked to try again in a quieter place.',
  },
  {
    q: 'Why does Xi reply to voice but not to typed messages?',
    a: 'The live assistant endpoint accepts a voice recording and returns a spoken reply with text. There is no text-message endpoint yet, so the composer explains this rather than faking a response. Voice chat works fully today.',
  },
  {
    q: 'Where is my conversation history stored?',
    a: 'Conversation history in the web chatbot is kept for the current browser session only. It clears when you close the tab. Saved emotion reports appear in Dashboard and are stored by the existing account service.',
  },
  {
    q: 'How do I change my plan or billing?',
    a: 'Open Plans to compare individual, doctor, corporate, school and sports plans. Existing checkout and payment providers are used unchanged. If you need a custom arrangement, contact the team using the address below.',
  },
  {
    q: 'Can I use Xi in another language?',
    a: 'Yes. Set the interface language in Settings. Voice conversations use the language you speak, and the assistant is prompted to answer accordingly.',
  },
  {
    q: 'How do I delete my account and data?',
    a: 'Open Settings and choose Deactivate, or go directly to the account deletion page. Deletion removes your account and stored reports and cannot be undone.',
  },
];

function FaqItem({ item, open, onToggle, id }) {
  return (
    <div className="xi-help-item">
      <button
        type="button"
        className={`xi-help-q${open ? ' is-open' : ''}`}
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={id}
      >
        {item.q}
        <LuChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xi-help-a" id={id}>
          {item.a}
        </div>
      )}
    </div>
  );
}

export default function Help() {
  const [openIndex, setOpenIndex] = useState(0);

  return (
    <XiPageShell
      eyebrow="Support"
      title="Help & support"
      lede="Answers to the questions people ask most, plus a direct line to the team."
    >
      <div className="xi-grid-2" style={{ alignItems: 'start' }}>
        <section className="xi-card" style={{ padding: '6px clamp(16px, 2.4vw, 26px)' }}>
          <h2 className="xi-section__title" style={{ paddingTop: 16 }}>
            Common questions
          </h2>
          {FAQS.map((item, index) => (
            <FaqItem
              key={item.q}
              id={`xi-faq-${index}`}
              item={item}
              open={openIndex === index}
              onToggle={() => setOpenIndex(openIndex === index ? -1 : index)}
            />
          ))}
        </section>

        <div style={{ display: 'grid', gap: 14 }}>
          <section className="xi-card" style={{ padding: 'clamp(18px, 2.4vw, 26px)' }}>
            <span className="xi-page__eyebrow" style={{ marginBottom: 8 }}>
              <i aria-hidden="true" />
              Contact
            </span>
            <h2 className="xi-section__title" style={{ marginBottom: 6 }}>
              Talk to a human
            </h2>
            <p className="xi-row__hint" style={{ marginBottom: 16 }}>
              For account, billing or clinical workflow questions, email the Xi team.
              We reply from the same address.
            </p>
            <a
              href="mailto:Oracle@Neurolonic.com"
              className="xi-btn xi-btn--primary"
              style={{ width: '100%' }}
            >
              <LuMail aria-hidden="true" />
              Oracle@Neurolonic.com
            </a>
          </section>

          <section className="xi-card" style={{ padding: 'clamp(18px, 2.4vw, 26px)' }}>
            <h2 className="xi-section__title" style={{ marginBottom: 12 }}>
              Shortcuts
            </h2>
            <div style={{ display: 'grid', gap: 8 }}>
              <Link to="/chatbot" className="xi-link-card">
                <span className="xi-link-card__icon" aria-hidden="true">
                  <LuMessageCircle />
                </span>
                <span className="xi-link-card__text">
                  <span className="xi-link-card__title">Talk to Xi</span>
                  <span className="xi-link-card__sub">Voice check-in with the assistant</span>
                </span>
              </Link>
              <Link to="/" className="xi-link-card">
                <span className="xi-link-card__icon" aria-hidden="true">
                  <LuMic />
                </span>
                <span className="xi-link-card__text">
                  <span className="xi-link-card__title">Predict an emotion</span>
                  <span className="xi-link-card__sub">Eight-second voice analysis</span>
                </span>
              </Link>
              <Link to="/plans" className="xi-link-card">
                <span className="xi-link-card__icon" aria-hidden="true">
                  <LuLayers />
                </span>
                <span className="xi-link-card__text">
                  <span className="xi-link-card__title">Plans &amp; billing</span>
                  <span className="xi-link-card__sub">Compare and manage subscriptions</span>
                </span>
              </Link>
              <Link to="/settings" className="xi-link-card">
                <span className="xi-link-card__icon" aria-hidden="true">
                  <LuShieldCheck />
                </span>
                <span className="xi-link-card__text">
                  <span className="xi-link-card__title">Privacy &amp; account</span>
                  <span className="xi-link-card__sub">Appearance, language, deletion</span>
                </span>
              </Link>
            </div>
          </section>
        </div>
      </div>
    </XiPageShell>
  );
}
